import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshEntitlements } = useAuth();
  const [state, setState] = useState("polling"); // polling | success | failed | expired
  const [detail, setDetail] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) { setState("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/billing/checkout/status/${sessionId}`, { credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error();
        if (d.payment_status === "paid") {
          setDetail(d);
          await refreshEntitlements();
          setState("success");
          return;
        }
        if (d.status === "expired") { setState("expired"); return; }
        if (attempts++ >= MAX_ATTEMPTS) { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts++ >= MAX_ATTEMPTS) setState("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, refreshEntitlements]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex items-center justify-center px-6">
      <div className="grain" />
      <div className="glass-card glass-card-featured max-w-lg w-full p-12 text-center" data-testid="checkout-return">
        {state === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-[#9fd3ff] animate-spin mx-auto" />
            <h1 className="mt-6 font-heading text-2xl font-light">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-zinc-500 font-light">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#7ee0a1] mx-auto" />
            <h1 className="mt-6 font-heading text-3xl font-light" data-testid="checkout-success">Welcome to MBM.</h1>
            <p className="mt-3 text-sm text-zinc-400 font-light">
              Your access is active{detail?.entitlements?.membership_name ? ` — ${detail.entitlements.membership_name}` : ""}.
              Every unlocked feature is ready for you.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/learn" className="btn-solid px-6 py-3 text-sm font-medium">Go to my dashboard</Link>
              <Link to="/intelligence" className="btn-glass text-white px-6 py-3 text-sm font-medium">Open Intelligence</Link>
            </div>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <XCircle className="h-12 w-12 text-[#ff7a7a] mx-auto" />
            <h1 className="mt-6 font-heading text-2xl font-light">
              {state === "expired" ? "Payment not completed" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-sm text-zinc-400 font-light">
              No charge was made. You can try again from the memberships page.
            </p>
            <Link to="/#pricing" className="mt-8 inline-block btn-solid px-6 py-3 text-sm font-medium">Back to memberships</Link>
          </>
        )}
      </div>
    </div>
  );
}
